import type { Lane } from "@/lib/track/lane";

/**
 * La séquence de morceaux, vue depuis le rouleau.
 *
 * # L'axe du rouleau
 *
 * Le rouleau et la minimap dessinent en ticks du transport, et le transport
 * est toujours dans les ticks du **morceau courant** : ses notes sont à leur
 * place sans rien changer, comme quand il est seul. Les autres morceaux de la
 * séquence — hors scène, sans piste — se dessinent au même endroit, décalés
 * de ce qui les sépare du courant : négatif pour ceux d'avant, au-delà de sa
 * durée pour ceux d'après. C'est l'**axe du rouleau** : tick 0 au début du
 * courant, et tout le reste relatif à lui. Les données ne bougent pas, seul
 * le dessin est décalé — voir `SongSequence` pour la décision.
 *
 * La minimap, elle, raisonne sur l'axe **global** — le début de la séquence
 * à zéro, les durées accumulées — parce qu'elle montre le tout et qu'un
 * changement de morceau ne doit pas la redessiner : `currentOffset` fait le
 * passage de l'un à l'autre.
 *
 * # Pourquoi une interface, et pas la séquence elle-même
 *
 * La vue Pixi est construite en asynchrone et reconstruite au remontage ; ce
 * qu'elle lit ne doit pas la lier à ce que le studio tient. Et un rouleau
 * construit sans séquence — les bancs, les tests — lit `EMPTY_LAYOUT` et ne
 * voit rien de plus qu'avant : c'est ce qui garantit qu'un seul morceau se
 * dessine exactement comme hier.
 */
export interface OffstageSong {
  /** Les lanes du morceau, telles que son contrôleur les tient. */
  readonly lanes: readonly Lane[];
  /** Son décalage sur l'axe du rouleau, en ticks : où tombe son tick 0. */
  readonly offsetTicks: number;
}

/** Le début d'un morceau sur le rouleau, et ce qu'on y écrit. */
export interface SongBoundary {
  /** Sur l'axe du rouleau. */
  readonly tick: number;
  /** « Prise 3 », le titre du morceau, ou « Morceau 3 » faute de mieux. */
  readonly label: string;
}

export interface SequenceLayout {
  /**
   * Change à chaque fois que ce qui suit change : un morceau ajouté, retiré,
   * ou la tête passée dans un autre. Les vues comparent ce nombre plutôt que
   * de reconstruire leurs listes à chaque image.
   */
  readonly version: number;
  /** Le tick 0 du morceau courant sur l'axe global, en ticks. Zéro pour un studio à un seul morceau. */
  readonly currentOffset: number;
  /** Les morceaux qui ne sont pas sous la tête, dans l'ordre. Vide pour un seul morceau. */
  offstage(): readonly OffstageSong[];
  /**
   * Les frontières à tracer, dans l'ordre, sur l'axe du rouleau — le début de
   * chaque morceau, courant compris. Vide pour un seul morceau : rien ne
   * borne ce qui n'a rien à côté.
   */
  boundaries(): readonly SongBoundary[];
}

const NO_SONGS: readonly OffstageSong[] = Object.freeze([]);
const NO_BOUNDARIES: readonly SongBoundary[] = Object.freeze([]);

/** Un studio sans séquence : rien hors scène, aucune frontière. */
export const EMPTY_LAYOUT: SequenceLayout = Object.freeze({
  version: 0,
  currentOffset: 0,
  offstage: () => NO_SONGS,
  boundaries: () => NO_BOUNDARIES,
});
